/* Tiloli header: compact-on-scroll + mega-menu open/close (hover, click, keyboard). */
class TiloliHeader extends HTMLElement {
  connectedCallback() {
    this.compactAt = 120;
    this.closeTimer = null;
    this.triggers = [...this.querySelectorAll('[data-mega-trigger]')];
    this.panels = [...this.querySelectorAll('[data-mega-panel]')];

    // expose the sticky header group's height so other sticky elements (filter bar) can offset below it
    const setHeight = () => {
      const grp = document.getElementById('header-group');
      document.documentElement.style.setProperty('--tlh-height', (grp ? grp.offsetHeight : this.offsetHeight) + 'px');
    };
    setHeight();
    window.addEventListener('resize', setHeight);

    if (this.dataset.sticky === 'true') {
      // Hysteresis: enter compact above ENTER, leave below LEAVE. The gap stops the jitter where
      // collapsing the top row shrinks the header, scrollY drops back under the threshold, and it re-toggles.
      this.enterAt = 160;
      this.leaveAt = 70;
      this._onScroll = (e) => {
        // ignore les scrolls synthétiques (dispatchés par du JS) → pas de toggle compact parasite
        if (e && e.isTrusted === false) return;
        const y = window.scrollY;
        // Only compact when the page is tall enough that collapsing the top row won't shrink the
        // document below the leave threshold (otherwise scroll clamps back under it → flicker).
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        const tall = maxScroll > this.enterAt + 160;
        // `tall` ne gate QUE l'entrée en compact. On NE quitte PAS sur `!tall` : sinon une variation
        // transitoire de scrollHeight (chargement résultats/images/prix → reflow → scrollY clampe →
        // event scroll) dé-compacte puis re-compacte = le menu « saute ». On quitte seulement sur un
        // vrai scroll vers le haut sous leaveAt.
        if (this.classList.contains('is-compact')) {
          if (y < this.leaveAt) { this.classList.remove('is-compact'); this.classList.remove('tlh--csearch-open'); }
        } else if (tall && y > this.enterAt) {
          this.classList.add('is-compact');
        }
        setHeight();
      };
      this._onScroll();
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    this.triggers.forEach((t) => {
      const id = t.getAttribute('data-mega-trigger');
      // L1 is a real link: open the mega on hover/focus, but let the click navigate to the collection.
      t.addEventListener('mouseenter', () => this.open(id));
      t.addEventListener('focus', () => this.open(id));
      t.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeNow(); });
    });

    // Entrées SANS niveau 2 (pas de trigger) : les survoler ferme le mega ouvert
    // (sinon le panneau de l'entrée précédente reste actif).
    this.querySelectorAll('.tlh__univ:not([data-mega-trigger])').forEach((a) => {
      a.addEventListener('mouseenter', () => this.closeNow());
    });

    // Keep panel open while hovered; close shortly after leaving trigger or panel.
    this.querySelector('.tlh__nav-wrap')?.addEventListener('mouseleave', () => this.scheduleClose());
    this.querySelector('.tlh__nav-wrap')?.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));

    // L2 tab switching inside each panel.
    this.panels.forEach((panel) => {
      const tabs = [...panel.querySelectorAll('.tlh__l2')];
      const views = [...panel.querySelectorAll('.tlh__l3-panel')];
      tabs.forEach((tab) => {
        const select = () => {
          tabs.forEach((x) => x.classList.remove('is-active'));
          views.forEach((x) => x.classList.remove('is-active'));
          tab.classList.add('is-active');
          const i = tab.getAttribute('data-l2');
          panel.querySelector(`.tlh__l3-panel[data-l2-panel="${i}"]`)?.classList.add('is-active');
        };
        tab.addEventListener('mouseenter', select);
        tab.addEventListener('focus', select);
      });
    });

    // ---- Mobile drawer ----
    this.drawer = this.querySelector('[data-drawer]');
    if (this.drawer) {
      this.querySelectorAll('[data-drawer-open]').forEach((b) => b.addEventListener('click', () => this.openDrawer()));
      this.drawer.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', () => this.closeDrawer()));

      // drill-down (L1 list -> L2 view, back to root)
      const views = [...this.drawer.querySelectorAll('.tlh-mview')];
      const panel = this.drawer.querySelector('.tlh-drawer__panel');
      this._showView = (name) => {
        views.forEach((v) => { v.hidden = v.getAttribute('data-view') !== name; });
        if (panel) panel.scrollTop = 0;
      };
      this.drawer.querySelectorAll('[data-drill]').forEach((b) =>
        b.addEventListener('click', () => this._showView(b.getAttribute('data-drill'))));
      this.drawer.querySelectorAll('[data-back]').forEach((b) =>
        b.addEventListener('click', () => this._showView('root')));

      // accordion
      this.drawer.querySelectorAll('[data-acc]').forEach((head) => head.addEventListener('click', () => {
        const open = head.getAttribute('aria-expanded') === 'true';
        head.setAttribute('aria-expanded', String(!open));
        const body = head.nextElementSibling;
        if (body) body.hidden = open;
      }));
    }

    // ---- Bascule burger quand le menu ne tient pas (pas assez de place) ----
    this._checkFit = () => {
      const nav = this.querySelector('.tlh__nav');
      const ns = this.querySelector('.tlh__nav-scroll');
      if (!nav || !ns) return;
      this.classList.remove('tlh--overflow');          // afficher la nav pour mesurer
      if (ns.scrollWidth > nav.clientWidth + 1) this.classList.add('tlh--overflow');
      setHeight();
    };
    this._checkFit();
    window.addEventListener('resize', this._checkFit);
    window.addEventListener('load', this._checkFit);
    [200, 600, 1200].forEach((t) => setTimeout(this._checkFit, t)); // recalcul après chargement des polices

    // ---- Recherche compacte : icône → champ (au scroll) ----
    this.csearchToggle = this.querySelector('[data-csearch]');
    if (this.csearchToggle) {
      this.csearchToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.classList.add('tlh--csearch-open');
        const inp = this.querySelector('.tlh__search--mini input');
        if (inp) setTimeout(() => inp.focus(), 60);
      });
      document.addEventListener('click', (e) => {
        if (!this.classList.contains('tlh--csearch-open')) return;
        if (!e.target.closest('.tlh__compact-actions')) this.classList.remove('tlh--csearch-open');
      });
    }

    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.closeNow(); this.closeDrawer(); this.classList.remove('tlh--csearch-open'); } });
  }

  openDrawer() {
    if (!this.drawer) return;
    if (this._showView) this._showView('root');
    this.drawer.hidden = false;
    // next frame so the transition runs
    requestAnimationFrame(() => this.drawer.classList.add('is-open'));
    document.documentElement.style.overflow = 'hidden';
    this.querySelectorAll('[data-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
  }

  closeDrawer() {
    if (!this.drawer || this.drawer.hidden) return;
    this.drawer.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    this.querySelectorAll('[data-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    const d = this.drawer;
    setTimeout(() => { if (!d.classList.contains('is-open')) d.hidden = true; }, 260);
  }

  disconnectedCallback() {
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    if (this._checkFit) { window.removeEventListener('resize', this._checkFit); window.removeEventListener('load', this._checkFit); }
  }

  isOpen(id) {
    const p = this.querySelector(`[data-mega-panel="${id}"]`);
    return p && !p.hidden;
  }

  open(id) {
    clearTimeout(this.closeTimer);
    this.panels.forEach((p) => { p.hidden = p.getAttribute('data-mega-panel') !== id; });
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', String(t.getAttribute('data-mega-trigger') === id)));
  }

  scheduleClose() { this.closeTimer = setTimeout(() => this.closeNow(), 140); }

  closeNow() {
    clearTimeout(this.closeTimer);
    this.panels.forEach((p) => { p.hidden = true; });
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
  }
}
customElements.define('tiloli-header', TiloliHeader);
